import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CtySpinner } from '../spinner/CtySpinner';
import { Icon } from '@iconify/react';
import { DitaHtmlProcessor } from './DitaHtmlProcessor';
import { MetadataInfoButton } from './MetadataInfoButton';

interface DitaZipFileViewerProps {
  ditaJsonUrl: string;
  fileName: string;
  advisorId: string;
  getPdfLink: (payload: any) => Promise<any>;
  metadataSourceId?: string;
  /** Topic source id from a conversation citation; selects that item in the menu. */
  initialTopicSourceId?: string;
}

interface TocItem {
  title: string;
  href?: string;
  source_id?: string;
  children?: TocItem[];
}

/**
 * DitaZipFileViewer Component
 * 
 * Renders DITA ZIP files with a sidebar navigation and main content area
 * 1. Fetches JSON TOC structure from presigned URL
 * 2. Renders hierarchical navigation in sidebar
 * 3. Displays selected topic content in main area using DitaHtmlProcessor
 */
export function DitaZipFileViewer({
  ditaJsonUrl,
  fileName,
  advisorId,
  getPdfLink,
  metadataSourceId,
  initialTopicSourceId,
}: DitaZipFileViewerProps) {
  console.log('🔍 DitaZipFileViewer: Props received', {
    ditaJsonUrl: ditaJsonUrl?.substring(0, 50) + '...',
    fileName,
    advisorId,
    getPdfLinkDefined: !!getPdfLink,
    getPdfLinkType: typeof getPdfLink,
  });

  const [tocData, setTocData] = useState<TocItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<TocItem | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const hasAutoSelected = useRef(false);

  // Helper to get path to first leaf item (for auto-expansion)
  const getPathToFirstLeaf = (item: TocItem, path: string[] = []): string[] | null => {
    const hasChildren = item.children && item.children.length > 0;
    
    if (!hasChildren && item.source_id) {
      return path;
    }
    
    if (item.children) {
      for (const child of item.children) {
        const childPath = getPathToFirstLeaf(child, [...path, item.title]);
        if (childPath) return childPath;
      }
    }
    
    return null;
  };

  // Fetch JSON TOC structure from presigned URL
  useEffect(() => {
    const fetchTocData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('🔄 DitaZipFileViewer: Fetching DITAMAP JSON from presigned URL:', ditaJsonUrl.substring(0, 100) + '...');
        const response = await fetch(ditaJsonUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch DITAMAP JSON: ${response.status} ${response.statusText}`);
        }

        const jsonData = await response.json();
        console.log('✅ DitaZipFileViewer: DITAMAP JSON loaded:', jsonData);

        setTocData(jsonData);

        // Auto-select the cited topic when present, otherwise the first leaf. Only once.
        if (!hasAutoSelected.current) {
          const citedMatch = initialTopicSourceId
            ? findTocItemBySourceId(jsonData, initialTopicSourceId)
            : null;
          const itemToSelect = citedMatch?.item ?? findFirstLeafWithSourceId(jsonData);
          const pathToExpand = citedMatch?.ancestors ?? getPathToFirstLeaf(jsonData);
          if (itemToSelect) {
            if (pathToExpand) {
              setExpandedItems(new Set(pathToExpand));
              console.log('🔓 Auto-expanded path:', pathToExpand);
            }
            setSelectedItem(itemToSelect);
            hasAutoSelected.current = true;
            console.log('✅ DitaZipFileViewer: Auto-selected topic:', itemToSelect.title);
          }
        }
      } catch (err) {
        const errorMessage = (err as Error).message || 'Failed to load DITAMAP structure';
        setError(errorMessage);
        console.error('❌ DitaZipFileViewer: Failed to fetch DITAMAP JSON:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (ditaJsonUrl) {
      fetchTocData();
    }
  }, [ditaJsonUrl, initialTopicSourceId]);

  const findTocItemBySourceId = (
    item: TocItem,
    sourceId: string,
    ancestors: string[] = []
  ): { item: TocItem; ancestors: string[] } | null => {
    const hasChildren = item.children && item.children.length > 0;
    if (item.source_id === sourceId && !hasChildren) {
      return { item, ancestors };
    }
    if (item.children) {
      for (const child of item.children) {
        const found = findTocItemBySourceId(child, sourceId, [...ancestors, item.title]);
        if (found) return found;
      }
    }
    return null;
  };

  // Find first leaf item with source_id for initial selection
  const findFirstLeafWithSourceId = (item: TocItem): TocItem | null => {
    // Check if this is a leaf item (no children) with a source_id
    const hasChildren = item.children && item.children.length > 0;
    if (!hasChildren && item.source_id) {
      return item;
    }
    // Recursively search children
    if (item.children) {
      for (const child of item.children) {
        const found = findFirstLeafWithSourceId(child);
        if (found) return found;
      }
    }
    return null;
  };

  // Toggle expand/collapse for navigation items
  // Use useCallback to prevent unnecessary re-renders
  const toggleExpand = useCallback((title: string) => {
    setExpandedItems((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(title)) {
        newExpanded.delete(title);
      } else {
        newExpanded.add(title);
      }
      return newExpanded;
    });
  }, []);

  // Render navigation tree recursively
  const renderNavItem = (item: TocItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.title);
    const isSelected = selectedItem?.title === item.title;
    const isLeafWithSourceId = !hasChildren && item.source_id; // Clickable leaf item

    return (
      <div key={`${item.title}-${item.source_id || 'no-id'}`} className="w-full">
        <div
          className={`
            flex items-center py-2 px-3 hover:bg-default-100
            ${isLeafWithSourceId ? 'cursor-pointer' : hasChildren ? 'cursor-pointer' : 'cursor-default'}
            ${isSelected ? 'bg-primary/10 border-l-4 border-primary' : ''}
          `}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={() => {
            if (hasChildren) {
              // Expand/collapse folder
              toggleExpand(item.title);
            } else if (item.source_id) {
              // Select leaf item with source_id
              console.log('📄 DitaZipFileViewer: Selected leaf item:', item.title, 'source_id:', item.source_id);
              setSelectedItem(item);
            }
          }}
        >
          {hasChildren && (
            <Icon
              icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
              className="w-4 h-4 mr-2 flex-shrink-0"
            />
          )}
          {!hasChildren && <div className="w-4 mr-2" />}
          <Icon
            icon={hasChildren ? 'mdi:folder' : 'mdi:file-document'}
            className={`w-4 h-4 mr-2 flex-shrink-0 ${isLeafWithSourceId ? 'text-primary' : 'text-default-400'}`}
          />
          <span className={`text-sm ${isSelected ? 'font-semibold text-primary' : isLeafWithSourceId ? 'text-default-700' : 'text-default-500'}`}>
            {item.title}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {item.children?.map((child) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Fetch presigned URL for selected topic's source_id
  const getTopicPresignedUrl = useCallback(async (sourceId: string): Promise<string | null> => {
    try {
      const payload = {
        source_ids: [{ source_id: sourceId }],
      };

      console.log('🔄 DitaZipFileViewer: Fetching presigned URL for topic:', sourceId);

      const apiResult = getPdfLink(payload);
      const response =
        typeof apiResult.unwrap === 'function'
          ? await apiResult.unwrap()
          : await apiResult;

      const presignedUrl = response?.data?.[0]?.full_source_presigned_url;

      if (!presignedUrl) {
        console.warn('⚠️ DitaZipFileViewer: No presigned URL returned for source_id:', sourceId);
        return null;
      }

      console.log('✅ DitaZipFileViewer: Got presigned URL for topic');
      return presignedUrl;
    } catch (error) {
      console.error('❌ DitaZipFileViewer: Failed to fetch presigned URL for topic:', sourceId, error);
      return null;
    }
  }, [getPdfLink]);

  // Loading state - Centered on entire page
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-content1 z-50">
        <div className="flex flex-col items-center gap-3">
          <CtySpinner size="lg" />
          <p className="text-sm text-default-500">Loading DITAMAP structure...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-danger bg-content1">
        <Icon icon="mdi:alert-circle" className="w-12 h-12 mb-2" />
        <p className="text-lg font-medium">Failed to Load DITAMAP</p>
        <p className="text-sm mt-2">{error}</p>
      </div>
    );
  }

  // No data state
  if (!tocData) {
    return (
      <div className="flex items-center justify-center h-full w-full text-default-500 bg-content1">
        <p className="text-lg">No DITAMAP data available</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-content1 overflow-hidden">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 w-full px-4 py-1 bg-content1 border-b border-default-200 flex items-center justify-between gap-2">
        <h1 className="font-semibold text-lg text-foreground truncate">{fileName}</h1>
        {metadataSourceId ? <MetadataInfoButton sourceId={metadataSourceId} /> : null}
      </div>

      {/* Main content area with sidebar and content - No vertical scroll on container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Navigation - Scrollable in Y direction */}
        <div className="w-80 flex-shrink-0 border-r border-default-200 bg-content2 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 p-2">
            <div className="px-3 py-2 bg-content1 rounded shadow-sm border border-default-200">
              <h2 className="font-semibold text-sm text-default-700 flex items-center">
                <Icon icon="mdi:table-of-contents" className="w-4 h-4 mr-2" />
                Table of Contents
              </h2>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2">
            {/* Render children directly without showing root item */}
            {tocData.children && tocData.children.length > 0 ? (
              tocData.children.map((child) => renderNavItem(child, 0))
            ) : (
              <div className="px-3 py-2 text-sm text-default-500">
                No topics available
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area - Scrollable in Y direction */}
        <div className="flex-1 overflow-hidden bg-white dark:bg-content1">
          {selectedItem && selectedItem.source_id ? (
            <DitaTopicViewer
              key={selectedItem.source_id}
              sourceId={selectedItem.source_id}
              title={selectedItem.title}
              advisorId={advisorId}
              getPdfLink={getPdfLink}
              getTopicPresignedUrl={getTopicPresignedUrl}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-default-500">
              <div className="text-center">
                <Icon icon="mdi:file-document-outline" className="w-16 h-16 mb-4 mx-auto text-default-400" />
                <p className="text-lg">Select a topic from the sidebar to view content</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * DitaTopicViewer - Displays content for a single DITA topic using DitaHtmlProcessor
 * Memoized to prevent re-renders when parent state changes (e.g., expandedItems)
 */
interface DitaTopicViewerProps {
  sourceId: string;
  title: string;
  advisorId: string;
  getPdfLink: (payload: any) => Promise<any>;
  getTopicPresignedUrl: (sourceId: string) => Promise<string | null>;
}

const DitaTopicViewer = React.memo(function DitaTopicViewer({
  sourceId,
  title,
  advisorId,
  getPdfLink,
  getTopicPresignedUrl,
}: DitaTopicViewerProps) {
  const [topicUrl, setTopicUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log('🔍 DitaTopicViewer: Props received', {
    sourceId,
    title,
    advisorId,
    getPdfLinkDefined: !!getPdfLink,
    getTopicPresignedUrlDefined: !!getTopicPresignedUrl,
  });

  useEffect(() => {
    const loadTopic = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('🔄 DitaTopicViewer: Loading topic with source_id:', sourceId);
        const url = await getTopicPresignedUrl(sourceId);

        if (!url) {
          throw new Error('Failed to get presigned URL for topic');
        }

        console.log('✅ DitaTopicViewer: Topic presigned URL received:', url.substring(0, 100) + '...');
        setTopicUrl(url);
      } catch (err) {
        const errorMessage = (err as Error).message || 'Failed to load topic';
        setError(errorMessage);
        console.error('❌ DitaTopicViewer: Failed to load topic:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTopic();
  }, [sourceId, getTopicPresignedUrl]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <CtySpinner size="lg" />
          <p className="text-sm text-default-500">Loading topic...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-danger">
        <Icon icon="mdi:alert-circle" className="w-12 h-12 mb-2" />
        <p className="text-lg font-medium">Failed to Load Topic</p>
        <p className="text-sm mt-2">{error}</p>
      </div>
    );
  }

  if (!topicUrl) {
    return (
      <div className="flex items-center justify-center h-full text-default-500">
        <p className="text-lg">No content available</p>
      </div>
    );
  }

  console.log('🎨 DitaTopicViewer: Rendering with topicUrl:', topicUrl?.substring(0, 100) + '...');
  console.log('🎨 DitaTopicViewer: About to render DitaHtmlProcessor with getPdfLink:', !!getPdfLink);
  
  if (!getPdfLink) {
    console.error('❌ DitaTopicViewer: getPdfLink is undefined!');
    return (
      <div className="flex items-center justify-center h-full text-danger">
        <p>Error: API configuration missing</p>
      </div>
    );
  }
  
  return (
    <div className="h-full w-full bg-white dark:bg-content1 overflow-y-auto">
      <DitaHtmlProcessor
        presignedUrl={topicUrl}
        getPdfLink={getPdfLink}
        advisorId={advisorId}
        lazyLoad={false}
        concurrencyLimit={5}
        onError={(error, sourceId) => {
          console.error('DitaTopicViewer error:', error, 'for source_id:', sourceId);
        }}
        className="h-full"
      />
    </div>
  );
});

